import { useMutation, useQueryClient } from '@tanstack/react-query';
import { RateAssetCommand } from '@/hooks/commands/asset/RateAssetCommand';
import { useAuthenticatedQueryFn } from '@/hooks/useAuthenticatedQuery';
import { useCommandContext } from '@/context/CommandContext';
import { useCurrentPage } from '@/hooks/useCurrentPage';
import { updateAsset } from '@/services/asset.service';
import { useToast } from '@/components/ui/use-toast';
import type { Asset } from '@/types/asset';
import { useTranslations } from 'next-intl';

export function useAssetRating() {
  const queryClient = useQueryClient();
  const tCommon = useTranslations('common');

  const { isDuplicatesPage } = useCurrentPage();
  const { toast } = useToast();
  const { apply } = useCommandContext();

  const updateAssetWithAuth = useAuthenticatedQueryFn(updateAsset);

  const updateAssetRating = useMutation({
    mutationFn: updateAssetWithAuth,
    onSuccess: (updated) => {
      // Update the asset in the cache
      queryClient.setQueryData<Asset>(['asset', updated.id], (old) => (old ? { ...old, ...updated } : old));

      // Update folder/album‑listing cache
      void queryClient.invalidateQueries({
        queryKey: isDuplicatesPage ? ['duplicates'] : ['assetData'],
      });
    },
    onError: (err: any) => {
      toast({ title: tCommon('error'), description: err.message });
    },
  });

  /**
   * Call this from your components to set a new rating.
   * `current` is the existing rating, `next` is the star they clicked.
   * If they click the same value again, we clear to zero.
   */
  const setRating = (id: string, current: number, next: number) => {
    const command = RateAssetCommand(id, current, next, (vars) => updateAssetRating.mutateAsync(vars));
    apply(command);
  };

  return { setRating, isPending: updateAssetRating.isPending };
}
